/**
 * AllInventoryAdjustments
 * Manage Inventory dialog — shows 3 action cards.
 * Add Stock → AllInventoryAddStepper (multi-type session).
 * Usage     → AllInventoryUsageStepper.
 * Transfer  → AllInventoryTransferStepper.
 */

import React, { useState } from 'react';
import {
  Dialog, DialogContent, Box, Typography, Card, Grid,
  IconButton, useTheme, alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  SwapHoriz as SwapHorizIcon,
  Tune as TuneIcon
} from '@mui/icons-material';
import AllInventoryAddStepper from './all-inventory-adjustments-components/Add/AllInventoryAddStepper';
import AllInventoryUsageStepper from './all-inventory-adjustments-components/Usage/AllInventoryUsageStepper';
import AllInventoryTransferStepper from './all-inventory-adjustments-components/Transfer/AllInventoryTransferStepper';

// ── Action card ───────────────────────────────────────────────────────────────

function ActionCard({ icon: Icon, title, description, hoverColor, onClick }) {
  const theme = useTheme();
  return (
    <Card
      onClick={onClick}
      sx={{
        p: 4, borderRadius: 3, cursor: 'pointer', height: '100%',
        border: `1px solid ${alpha(theme.palette.grey[400], 0.5)}`,
        bgcolor: 'background.paper',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: `0 12px 32px ${alpha(hoverColor, 0.2)}`,
          border: `2px solid ${hoverColor}`,
          bgcolor: alpha(hoverColor, 0.02)
        }
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{
          width: 72, height: 72, borderRadius: '50%', mx: 'auto', mb: 2.5,
          bgcolor: alpha(hoverColor, 0.1),
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <Icon sx={{ fontSize: 36, color: hoverColor }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, fontSize: '1.1rem' }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
          {description}
        </Typography>
      </Box>
    </Card>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AllInventoryAdjustments({
  open, onClose, companyId, locations, suppliers, onSuccess
}) {
  const theme = useTheme();
  const [addStepperOpen,      setAddStepperOpen]      = useState(false);
  const [usageStepperOpen,    setUsageStepperOpen]    = useState(false);
  const [transferStepperOpen, setTransferStepperOpen] = useState(false);

  const handleClose = () => {
    setAddStepperOpen(false);
    setUsageStepperOpen(false);
    setTransferStepperOpen(false);
    onClose();
  };

  const handleAddSuccess = (msg, severity) => {
    onSuccess?.(msg, severity);
  };

  const handleUsageSuccess = (msg, severity) => {
    onSuccess?.(msg, severity);
  };

  return (
    <>
      {/* Action picker — hidden while a stepper is open */}
      <Dialog
        open={open && !addStepperOpen && !usageStepperOpen && !transferStepperOpen}
        onClose={(_, reason) => { if (reason !== 'backdropClick') handleClose(); }}
        disableEscapeKeyDown
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3, border: 'none',
            background: `linear-gradient(135deg, #fafafa, #f5f5f5)`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            overflow: 'hidden'
          }
        }}
      >
        {/* Header */}
        <Box sx={{
          p: 3,
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{
              p: 1.5, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}>
              <TuneIcon sx={{ fontSize: 32 }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 500, mb: 0.5 }}>Manage Inventory</Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Choose the type of inventory adjustment you want to make
              </Typography>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.04)', transform: 'rotate(90deg)' }, transition: 'all 0.2s ease' }}
          >
            <CloseIcon sx={{ fontSize: 28 }} />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 4, minHeight: 360, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <Typography variant="body2" sx={{ mb: 4, color: 'text.secondary', textAlign: 'center', fontSize: '0.95rem' }}>
            Choose the type of inventory adjustment you want to make
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <ActionCard
                icon={AddIcon} title="Add Stock"
                description="Increase inventory levels — add to one or multiple types in a single session"
                hoverColor="#388e3c"
                onClick={() => setAddStepperOpen(true)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ActionCard
                icon={RemoveIcon} title="Usage"
                description="Remove units from inventory due to usage or consumption"
                hoverColor="#d32f2f"
                onClick={() => setUsageStepperOpen(true)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <ActionCard
                icon={SwapHorizIcon} title="Transfer"
                description="Move inventory between different locations"
                hoverColor="#1976d2"
                onClick={() => setTransferStepperOpen(true)}
              />
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>

      {/* Multi-type Add Stock session */}
      <AllInventoryAddStepper
        open={addStepperOpen}
        onBack={() => setAddStepperOpen(false)}
        onClose={() => { setAddStepperOpen(false); onClose(); }}
        companyId={companyId}
        locations={locations}
        suppliers={suppliers}
        onSuccess={handleAddSuccess}
      />

      {/* Multi-type Usage session */}
      <AllInventoryUsageStepper
        open={usageStepperOpen}
        onBack={() => setUsageStepperOpen(false)}
        onClose={() => { setUsageStepperOpen(false); onClose(); }}
        companyId={companyId}
        locations={locations}
        onSuccess={handleUsageSuccess}
      />

      {/* Multi-type Transfer session */}
      <AllInventoryTransferStepper
        open={transferStepperOpen}
        onBack={() => setTransferStepperOpen(false)}
        onClose={() => { setTransferStepperOpen(false); onClose(); }}
        companyId={companyId}
        locations={locations}
        onSuccess={onSuccess}
      />
    </>
  );
}
